import { useMemo, useState } from 'react';
import { Table, Input, Select, Button, Space, Modal, Form, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useDocumentVersions } from '@/entities/document/api/hooks';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { antTablePagination, antTableScroll } from '@/shared/constants/antTable';
import { SearchInput } from '@/shared/ui/SearchInput';
import type { DocumentVersion } from '@/entities/document/model/documentSchema';
import type { Status } from '@/entities/user/model/userSchema';
import { versionDocumentStatusLabel, VERSION_SCOPE_FILTER_OPTIONS } from '../constants';
import s from './VersionsTab.module.scss';

export function VersionsTab() {
  const { data, isLoading } = useDocumentVersions();
  const [search, setSearch] = useState('');
  const [typeScope, setTypeScope] = useState<string>('local');
  const [addedVersions, setAddedVersions] = useState<DocumentVersion[]>([]);
  const [versionModal, setVersionModal] = useState(false);
  const [vForm] = Form.useForm();

  const allVersions = useMemo(() => [...(data ?? []), ...addedVersions], [data, addedVersions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allVersions.filter((r) => {
      const matchText =
        !q ||
        r.documentTitle.toLowerCase().includes(q) ||
        r.changes.toLowerCase().includes(q) ||
        r.author.toLowerCase().includes(q);
      const matchLocal = typeScope !== 'local' || r.isLocal;
      return matchText && matchLocal;
    });
  }, [allVersions, search, typeScope]);

  const versionColumns: ColumnsType<DocumentVersion> = [
    { title: 'Документ', dataIndex: 'documentTitle', key: 'doc' },
    { title: 'Версия', dataIndex: 'version', key: 'ver' },
    { title: 'Дата публикации', dataIndex: 'publishDate', key: 'date' },
    { title: 'Автор', dataIndex: 'author', key: 'author' },
    { title: 'Изменения', dataIndex: 'changes', key: 'changes', ellipsis: true },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (st: Status) => <StatusBadge status={st} label={versionDocumentStatusLabel(st)} />,
    },
  ];

  const submitVersion = () => {
    vForm.validateFields().then((v) => {
      const rec: DocumentVersion = {
        id: -Date.now(),
        documentTitle: v.documentTitle as string,
        version: v.version as string,
        publishDate: v.publishDate as string,
        author: v.author as string,
        changes: v.changes as string,
        status: 'ok',
        isLocal: true,
      };
      setAddedVersions((prev) => [rec, ...prev]);
      setVersionModal(false);
      vForm.resetFields();
      message.success('Версия документа создана');
    });
  };

  return (
    <>
      <Space wrap className={s.root__filters}>
        <SearchInput
          allowClear
          className={s['root__version-search']}
          placeholder="Поиск по документу, изменениям…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          className={s['root__version-type-select']}
          value={typeScope}
          onChange={setTypeScope}
          options={[...VERSION_SCOPE_FILTER_OPTIONS]}
        />
        <Button
          type="primary"
          onClick={() => {
            vForm.resetFields();
            setVersionModal(true);
          }}
        >
          + Новая версия
        </Button>
      </Space>
      <Table
        loading={isLoading}
        dataSource={filtered}
        columns={versionColumns}
        rowKey="id"
        size="small"
        pagination={{ ...antTablePagination }}
        scroll={antTableScroll}
      />
      <Modal
        title="Создать версию документа"
        open={versionModal}
        onCancel={() => {
          setVersionModal(false);
          vForm.resetFields();
        }}
        onOk={submitVersion}
        okText="Создать"
        destroyOnHidden
      >
        <Form form={vForm} layout="vertical">
          <Form.Item name="documentTitle" label="Документ" rules={[{ required: true, message: 'Укажите документ' }]}>
            <Input placeholder="Наименование документа" />
          </Form.Item>
          <Form.Item name="version" label="Версия" rules={[{ required: true, message: 'Укажите версию' }]}>
            <Input placeholder="Напр. v3.1" />
          </Form.Item>
          <Form.Item name="publishDate" label="Дата публикации" rules={[{ required: true }]}>
            <Input placeholder="ДД.ММ.ГГГГ" />
          </Form.Item>
          <Form.Item name="author" label="Автор" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="changes" label="Описание изменений" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
